import dayjs from "dayjs"
import i18n, { type TFunction } from "i18next"

import { RoutesPaths } from "../../../constants/routes.ts"
import {
  APPOINTMENT_TYPES,
  appointmentTypeLabelKey
} from "../../Calendar/CalendarPage/constants/appointment-types.ts"
import type { AppointmentType } from "../../Calendar/types/types.ts"
import {
  LEAD_STATUSES_MAP,
  type LeadStatusesValues
} from "../../Lead/constants/lead-statuses.ts"
import { PROJECT_STATUSES_OBJ } from "../../Projects/constants/project-statuses.ts"
import {
  STATUS_REFUSAL_CODES,
  type StatusRecordKind,
  type StatusRefusal,
  type StatusSuggestion,
  type StatusTransition
} from "../types/status-automation.types.ts"

export const STATUS_HISTORY_PARAM = "status_history"
export const STATUS_SUGGESTION_PARAM = "suggestion"

/** The English label the status maps key on, or null for an unknown value. */
export const statusLabelOf = (
  kind: StatusRecordKind,
  status: number | null | undefined
): string | null => {
  if (status === null || status === undefined) return null
  const label =
    kind === "project"
      ? PROJECT_STATUSES_OBJ[status]?.label
      : LEAD_STATUSES_MAP[status as LeadStatusesValues]?.label
  return label ?? null
}

/** The label a reader sees: a lead stage through `leadStages.*`, as the header does. */
export const statusDisplay = (
  kind: StatusRecordKind,
  status: number | null | undefined,
  t: TFunction
): string => {
  const label = statusLabelOf(kind, status)
  if (!label) return t("statusAutomation.unknownStatus")
  return kind === "lead" ? t(`leadStages.${label}`, label) : label
}

const MEETING_EVENTS = new Set(["lead_meeting_booked", "lead_meeting_completed"])

const ENGLISH_MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
]

// The server writes the day in English ("Oct 12, 2026"); a reader sees it in
// their own language ("12. Okt."). Anything else is shown as it came.
export const localMeetingDay = (day: string, locale?: string): string => {
  const match = /^([A-Za-z]{3})\s+(\d{1,2}),\s*(\d{4})$/.exec(day)
  const month = match ? ENGLISH_MONTHS.indexOf(match[1]) : -1
  if (!match || month < 0) return day.replace(/,\s*\d{4}$/, "")
  const date = new Date(Date.UTC(Number(match[3]), month, Number(match[2])))
  try {
    return new Intl.DateTimeFormat(locale || i18n.language || "en", {
      day: "numeric",
      month: "short",
      timeZone: "UTC"
    }).format(date)
  } catch {
    return day.replace(/,\s*\d{4}$/, "")
  }
}

// A meeting's label is "<its title> · Oct 12, 2026". The title is free text
// (often a whole sentence), so the chip keeps only the day, without the year.
const meetingDay = (label: string) => {
  const day = label.split(" · ").pop()?.trim() ?? ""
  return day ? localMeetingDay(day) : null
}

// An event the client has no words for still reads as something true. With
// the document's own label it is named ("Proposal PROP-2026-041 sent").
export const eventDisplay = (
  event: string | null | undefined,
  t: TFunction,
  label?: string | null,
  category?: string | null
): string | null => {
  if (!event) return null
  const key = event.replace(/\./g, "_")
  if (label && MEETING_EVENTS.has(key)) {
    const day = meetingDay(label)
    // "Site Visit on Oct 12 completed": the type the calendar already names.
    if (day && (APPOINTMENT_TYPES as readonly string[]).includes(category ?? "")) {
      return t(`statusAutomation.eventsWithCategory.${key}`, {
        name: t(appointmentTypeLabelKey(category as AppointmentType)),
        day
      })
    }
    label = day
  }
  if (label) {
    const named = t(`statusAutomation.eventsWithLabel.${key}`, {
      label,
      defaultValue: ""
    })
    if (named) return named
  }
  return t(`statusAutomation.events.${key}`, { defaultValue: "" }) || null
}

export const readStatusRefusal = (data: unknown): StatusRefusal | null => {
  const body = data as
    | (Partial<StatusRefusal> & { details?: Partial<StatusRefusal> })
    | null
    | undefined
  const refusal = body?.code ? body : body?.details
  return refusal?.code
    ? { code: String(refusal.code), detail: refusal.detail }
    : null
}

/** A 409 in words: ours for a known code, else the server's own sentence. */
export const refusalMessage = (refusal: StatusRefusal, t: TFunction): string =>
  (STATUS_REFUSAL_CODES as readonly string[]).includes(refusal.code)
    ? t(`statusAutomation.refusals.${refusal.code}`)
    : refusal.detail || t("statusAutomation.refusals.generic")

/** A refusal that means the suggestion is gone for good, not a passing failure. */
export const closesSuggestion = (refusal: StatusRefusal | null) =>
  refusal?.code === "suggestion_closed" || refusal?.code === "suggestion_stale"

export const isUndoAvailable = (
  transition: Pick<StatusTransition, "undo_available_until">,
  now: dayjs.Dayjs = dayjs()
) =>
  !!transition.undo_available_until &&
  now.isBefore(dayjs(transition.undo_available_until))

// Once the fields are filled the server says so, whatever it still lists.
export const blocksTheMove = (suggestion: StatusSuggestion) =>
  suggestion.target !== null &&
  suggestion.reason !== "requirements_met" &&
  suggestion.missing_field_names.length > 0

// A project's prerequisites live on four tabs; the first missing one decides.
const PROJECT_FIELD_SECTION: Record<
  string,
  "overviewTab" | "milestones" | "finance" | "files"
> = {
  project_manager: "overviewTab",
  total_budget: "overviewTab",
  signed_contract: "files",
  milestone_schedule: "milestones",
  phases: "milestones",
  start_date: "milestones",
  end_date: "milestones",
  subcontractor: "finance"
}

/**
 * Where "add <field>" goes. A lead uses the missing-fields deep link the status
 * selector already answers with (it marks the cards and offers the retry); a
 * project opens the tab the first missing prerequisite is set on.
 */
export const missingFieldsHref = (
  kind: StatusRecordKind,
  recordId: string,
  suggestion: StatusSuggestion
): string | null => {
  const fields = suggestion.missing_field_names
  if (!fields.length || suggestion.target === null) return null
  if (kind === "lead") {
    const params = new URLSearchParams({
      next_status: String(suggestion.target),
      missing: fields.join(",")
    })
    return `${RoutesPaths.lead.view(recordId)}?${params.toString()}`
  }
  const section = fields.map((field) => PROJECT_FIELD_SECTION[field]).find(Boolean)
  const paths = RoutesPaths.project.view(recordId)
  if (section === "files") return paths.files()
  return section ? paths[section] : paths.overviewTab
}

/** Where a notice about an automatic move opens: the record, with its history up. */
export const statusHistoryHref = (kind: StatusRecordKind, recordId: string) =>
  kind === "lead"
    ? `${RoutesPaths.lead.view(recordId)}?${STATUS_HISTORY_PARAM}=open`
    : `${RoutesPaths.project.view(recordId).overview}?${STATUS_HISTORY_PARAM}=open`

export const suggestionHref = (
  kind: StatusRecordKind,
  recordId: string,
  suggestionId?: string
) => {
  const base =
    kind === "lead"
      ? RoutesPaths.lead.view(recordId)
      : RoutesPaths.project.view(recordId).overview
  return suggestionId
    ? `${base}?${STATUS_SUGGESTION_PARAM}=${encodeURIComponent(suggestionId)}`
    : base
}
